// Add / edit a child. In "add" mode it collects username (with a live
// availability check), password, name, avatar, and grade. In "edit" mode it
// edits name, avatar, and grade only — username is immutable and password is
// rotated via the separate reset action.

import { useT } from '@fajrarabi/i18n/react';
import { Alert, Button, Dialog, DialogContent, Field, Input } from '@fajrarabi/ui';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';

import { AvatarPicker } from './AvatarPicker';

import { ApiError } from '~/lib/api';
import {
  checkUsername,
  createKid,
  updateKid,
  type CreateKidInput,
  type Grade,
  type Kid,
  type UpdateKidInput,
} from '~/lib/family';

const GRADES: Grade[] = ['kg2', 'g1', 'g2', 'g3', 'g4', 'g5', 'g6'];
const GRADE_LABELS: Record<Grade, string> = {
  kg2: 'KG2',
  g1: 'Grade 1',
  g2: 'Grade 2',
  g3: 'Grade 3',
  g4: 'Grade 4',
  g5: 'Grade 5',
  g6: 'Grade 6',
};

type Availability = { state: 'idle' | 'checking' | 'available' | 'taken'; suggestions: string[] };

export function KidFormModal({
  open,
  onOpenChange,
  kid,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** When provided, the modal is in edit mode. */
  kid?: Kid | null;
}) {
  const t = useT();
  const queryClient = useQueryClient();
  const isEdit = Boolean(kid);

  const [displayName, setDisplayName] = useState('');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [avatar, setAvatar] = useState('lion');
  const [grade, setGrade] = useState<Grade | ''>('');
  const [error, setError] = useState<string | null>(null);
  const [availability, setAvailability] = useState<Availability>({
    state: 'idle',
    suggestions: [],
  });

  // Reset the form whenever the modal opens (or the target kid changes).
  useEffect(() => {
    if (!open) return;
    setError(null);
    setAvailability({ state: 'idle', suggestions: [] });
    if (kid) {
      setDisplayName(kid.displayName ?? '');
      setAvatar(kid.avatar ?? 'lion');
      setGrade((kid.grade as Grade | null) ?? '');
      setUsername('');
      setPassword('');
    } else {
      setDisplayName('');
      setUsername('');
      setPassword('');
      setAvatar('lion');
      setGrade('');
    }
  }, [open, kid]);

  // Debounced username availability check (add mode only).
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    if (isEdit) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    const candidate = username.trim().toLowerCase();
    if (candidate.length < 3) {
      setAvailability({ state: 'idle', suggestions: [] });
      return;
    }
    setAvailability({ state: 'checking', suggestions: [] });
    debounceRef.current = setTimeout(() => {
      void checkUsername(candidate)
        .then((res) =>
          setAvailability(
            res.available
              ? { state: 'available', suggestions: [] }
              : { state: 'taken', suggestions: res.suggestions },
          ),
        )
        .catch(() => setAvailability({ state: 'idle', suggestions: [] }));
    }, 400);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [username, isEdit]);

  const mutation = useMutation({
    mutationFn: async () => {
      if (isEdit && kid) {
        const patch: UpdateKidInput = {
          displayName: displayName.trim(),
          avatar,
          grade: grade === '' ? null : grade,
        };
        await updateKid(kid.id, patch);
      } else {
        const input: CreateKidInput = {
          username: username.trim().toLowerCase(),
          password,
          displayName: displayName.trim(),
          avatar,
          grade: grade === '' ? null : grade,
        };
        await createKid(input);
      }
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['family', 'kids'] });
      onOpenChange(false);
    },
    onError: (err) => {
      setError(err instanceof ApiError ? err.message : t('app.common.error_generic'));
    },
  });

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    mutation.mutate();
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        title={isEdit ? t('app.parent.edit_kid_title') : t('app.parent.add_kid_title')}
        className="w-[min(32rem,calc(100vw-2rem))]"
      >
        <form onSubmit={handleSubmit} className="space-y-4">
          <Field label={t('app.parent.field_display_name')}>
            {(id) => (
              <Input
                id={id}
                required
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
              />
            )}
          </Field>

          {!isEdit && (
            <>
              <Field
                label={t('app.parent.field_username')}
                hint={t('app.parent.field_username_hint')}
              >
                {(id) => (
                  <Input
                    id={id}
                    required
                    autoCapitalize="none"
                    spellCheck={false}
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                  />
                )}
              </Field>

              {availability.state === 'available' && (
                <p className="text-xs font-semibold text-leaf">
                  ✓ {t('app.parent.username_available')}
                </p>
              )}
              {availability.state === 'taken' && (
                <div className="text-xs text-ink-700">
                  <p className="font-semibold text-heart">{t('app.parent.username_taken')}</p>
                  <div className="mt-1 flex flex-wrap gap-1.5">
                    {availability.suggestions.map((s) => (
                      <button
                        key={s}
                        type="button"
                        onClick={() => setUsername(s)}
                        className="rounded-full bg-cream-100 px-2.5 py-1 font-medium text-burgundy-500 ring-1 ring-burgundy-500/10 hover:bg-cream-200"
                      >
                        {s}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              <Field
                label={t('app.parent.field_password')}
                hint={t('app.parent.field_password_hint')}
              >
                {(id) => (
                  <Input
                    id={id}
                    type="password"
                    required
                    autoComplete="new-password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                  />
                )}
              </Field>
            </>
          )}

          <Field label={t('app.parent.field_avatar')}>
            {() => <AvatarPicker value={avatar} onChange={setAvatar} />}
          </Field>

          <Field label={t('app.parent.field_grade')}>
            {(id) => (
              <select
                id={id}
                value={grade}
                onChange={(e) => setGrade(e.target.value as Grade | '')}
                className="h-11 w-full rounded-2xl border border-burgundy-500/15 bg-cream-50 px-4 text-ink-900 focus:outline-none focus:ring-2 focus:ring-burgundy-500/40"
              >
                <option value="">{t('app.parent.grade_none')}</option>
                {GRADES.map((g) => (
                  <option key={g} value={g}>
                    {GRADE_LABELS[g]}
                  </option>
                ))}
              </select>
            )}
          </Field>

          {error && <Alert tone="danger">{error}</Alert>}

          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="secondary" onClick={() => onOpenChange(false)}>
              {t('app.common.cancel')}
            </Button>
            <Button type="submit" loading={mutation.isPending}>
              {t('app.common.save')}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
